import { cloneElement } from 'react';
import styled, { css } from 'styled-components';
import { switchProp } from 'styled-tools';
import { colorUtils, layout, colors } from '@prisjakt/ui-base';
import { ThemeType } from '@prisjakt/ui-base/styles/themes/default';
import { IconPosition, Size, Variant, Color } from './types';

const buttonColors = (theme: ThemeType) => ({
  primary: {
    text: theme.buttons.primary.text,
    hoverText: theme.buttons.primary.textHover,
    background: theme.buttons.primary.bg,
    hoverBackground: colorUtils.shadeColor(theme.buttons.primary.bg, -20),
    activeBackground: theme.buttons.primary.bgActive,
    iconFill: theme.buttons.primary.bg,
  },
  secondary: {
    text: theme.buttons.secondary.text,
    hoverText: theme.buttons.secondary.textHover,
    background: theme.buttons.secondary.bg,
    hoverBackground: colorUtils.shadeColor(theme.buttons.secondary.bg, -20),
    iconFill: theme.buttons.secondary.bg,
  },
  tertiary: {
    text: theme.buttons.tertiary.text,
    hoverText: theme.buttons.tertiary.textHover,
    background: theme.buttons.tertiary.bg,
    hoverBackground: colorUtils.shadeColor(theme.buttons.tertiary.bg, -20),
    iconFill: theme.buttons.tertiary.bg,
  },
  quarternary: {
    text: theme.buttons.quarternary.text,
    hoverText: theme.buttons.quarternary.textHover,
    background: theme.buttons.quarternary.bg,
    hoverBackground: colorUtils.shadeColor(theme.buttons.quarternary.bg, 40),
    iconFill: theme.buttons.quarternary.bg,
  },
  positive: {
    text: theme.buttons.positive.text,
    hoverText: theme.buttons.positive.textHover,
    background: theme.buttons.positive.bg,
    hoverBackground: colorUtils.shadeColor(theme.buttons.positive.bg, -20),
    iconFill: theme.buttons.positive.bg,
  },
  negative: {
    text: theme.buttons.negative.text,
    hoverText: theme.buttons.negative.textHover,
    background: theme.buttons.negative.bg,
    hoverBackground: colorUtils.shadeColor(theme.buttons.negative.bg, -20),
    iconFill: theme.buttons.negative.bg,
  },
  link: {
    text: theme.buttons.link.text,
    hoverText: theme.buttons.link.textHover,
    background: theme.buttons.link.bg,
    hoverBackground: theme.buttons.link.bg,
    iconFill: theme.buttons.link.text,
  },
  disabled: {
    text: theme.buttons.disabled.text,
    hoverText: theme.buttons.disabled.textHover,
    background: theme.buttons.disabled.bg,
    hoverBackground: theme.buttons.disabled.bg,
    iconFill: theme.buttons.disabled.bg,
  },
});

const fontSize: { [s in Size]: string } = {
  small: layout.measurements.fontSizeSmall,
  medium: layout.measurements.fontSizeNormal,
  large: layout.measurements.fontSizeLarge,
};

const iconSize: { [s in Size]: number } = {
  small: 19,
  medium: 24,
  large: 29,
};

const padding: { [s in Size]: string } = {
  small: '6px 10px',
  medium: '8px 20px',
  large: '8px 20px',
};

type ButtonProps = {
  color: Color;
  disabled: boolean;
  size: Size;
  isLink: boolean;
  theme: ThemeType;
  active: boolean;
  scaleIcon: any;
};

// Cloning allows us to dynamically render an element based on its props
// For example: We render <a /> if we pass `href` to the component
// Only forwarding `rest` to the cloned element stops the Unknown Prop warnings
// in the test suites. Should be replaced by `shouldForwardProp` when this
// feature lands in Styled Components.
export const Button = styled(
  ({ component, isLink, scaleIcon, iconPosition, ...rest }) =>
    cloneElement(component, rest)
)<ButtonProps>(
  ({ color, disabled, size, isLink, theme, active, scaleIcon }) => css`
    border-radius: 4px;
    font-weight: 600;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    vertical-align: middle;
    white-space: nowrap;

    font-size: ${fontSize[size]};
    padding: ${padding[size]};

    svg {
      transform: ${scaleIcon
        ? `scale(${iconSize[size] / iconSize.medium})`
        : 'none'};
      path {
        fill: currentColor;
      }
    }

    cursor: ${disabled ? 'default' : 'pointer'};
    pointer-events: ${disabled ? 'none' : 'inherit'};
    opacity: ${disabled ? '0.5' : '1'};
    transition: background ${layout.animations.buttonTransition},
      color ${layout.animations.buttonTransition};

    &:hover,
    &:focus {
      background: ${buttonColors(theme)[color].hoverBackground};
      color: ${buttonColors(theme)[color].hoverText};
    }

    &:active {
      background: ${buttonColors(theme)[color].background};
    }

    /* Reset non-button elements like links <a role="button" /> */
    &[type='button'] {
      appearance: none;
      outline: none;
    }

    ${switchProp('variant', {
      default: css<ButtonProps>`
        color: ${buttonColors(theme)[color].text};
        background: ${buttonColors(theme)[color].background};
      `,

      icon: css<ButtonProps>`
        border-radius: 100%;
        padding: 0;
        height: ${iconSize[size] + 10}px;
        width: ${iconSize[size] + 10}px;
        &:focus,
        &:hover {
          background: ${colorUtils.setRgbaAlpha(
            buttonColors(theme)[color].iconFill,
            '0.2'
          )};
        }

        &:active {
          background: transparent;
        }

        svg {
          transform: ${scaleIcon
            ? `scale(${iconSize[size] / iconSize.medium})`
            : 'none'};
          path {
            fill: ${buttonColors(theme)[color].iconFill};
          }
        }
      `,

      iconOutlined: css<ButtonProps>`
        border-radius: 100%;
        padding: 0;
        height: ${iconSize[size] + 10};
        width: ${iconSize[size] + 10};
        background: ${active
          ? colorUtils.setRgbaAlpha(buttonColors(theme)[color].iconFill, '0.2')
          : colors.semiWhite};
        svg {
          transform: scale(${iconSize[size] / iconSize.medium});
          path {
            fill: ${active
              ? buttonColors(theme)[color].iconFill
              : colors.darkGray1};
          }
        }
        &:focus,
        &:hover,
        &:active {
          background: ${colorUtils.setRgbaAlpha(
            buttonColors(theme)[color].iconFill,
            '0.2'
          )};
          svg {
            path {
              fill: ${buttonColors(theme)[color].iconFill};
            }
          }
        }
      `,

      text: css<ButtonProps>`
        background: ${(active && buttonColors(theme)[color].activeBackground) ||
          'transparent'};
        color: ${// eslint-disable-next-line no-nested-ternary
        disabled
          ? buttonColors(theme).disabled.background
          : color === 'link'
          ? buttonColors(theme).link.text
          : buttonColors(theme)[color].background};
        white-space: normal;

        &:focus,
        &:hover {
          background: ${(active &&
            buttonColors(theme)[color].activeBackground) ||
            'transparent'};
          color: ${colorUtils.setRgbaAlpha(
            color === 'link'
              ? buttonColors(theme).link.text
              : buttonColors(theme)[color].background,
            '0.7'
          )};
        }

        &:active {
          background: transparent;
          color: ${buttonColors(theme).link.text};
        }
      `,

      outlined: css<ButtonProps>`
        background: transparent;
        border: ${color === 'link' ? 0 : 'solid 1px'};
        border-color: ${disabled
          ? buttonColors(theme).disabled.background
          : buttonColors(theme)[color].background};

        color: ${// eslint-disable-next-line no-nested-ternary
        disabled
          ? buttonColors(theme).disabled.background
          : color === 'link'
          ? buttonColors(theme).link.text
          : buttonColors(theme)[color].background};

        &:focus,
        &:hover {
          background: ${color === 'link'
            ? buttonColors(theme).link.background
            : colorUtils.setRgbaAlpha(
                buttonColors(theme)[color].background,
                '0.2'
              )};
          border-color: ${buttonColors(theme)[color].background};
          color: ${color === 'link'
            ? buttonColors(theme).link.hoverText
            : buttonColors(theme)[color].background};
        }

        &:active {
          background: transparent;
        }
      `,
    })}
  `
);

type InnerProps = {
  variant: Variant;
  truncate: boolean;
  iconPosition: IconPosition;
};

export const Inner = styled.span<InnerProps>(({ variant, truncate }) => [
  {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    lineHeight: '1.75',
    width: '100%',
  },

  variant !== 'icon' &&
    switchProp('iconPosition', {
      top: {
        flexDirection: 'column',
      },
      bottom: {
        flexDirection: 'column',
      },
      left: {
        flexDirection: 'row',
      },
      right: {
        flexDirection: 'row',
      },
    }),

  truncate &&
    css`
      display: inline;
      text-align: center;
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
    `,
]);

type IconWrapperProps = {
  size: Size;
  iconPosition: IconPosition;
  noMargin?: boolean;
};

export const IconWrapper = styled.span<IconWrapperProps>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: ${p => iconSize[p.size]}px;
  height: ${p => iconSize[p.size]}px;
  font-size: ${p => iconSize[p.size]}px;

  ${switchProp('iconPosition', {
    top: css<IconWrapperProps>`
      margin-bottom: ${p => (p.noMargin ? 0 : '5px')};
    `,

    bottom: css<IconWrapperProps>`
      margin-top: ${p => (p.noMargin ? 0 : '5px')};
    `,

    left: css<IconWrapperProps>`
      margin-right: ${p => (p.noMargin ? 0 : '5px')};
    `,

    right: css<IconWrapperProps>`
      margin-left: ${p => (p.noMargin ? 0 : '5px')};
    `,
  })}
`;
